import { useMemo } from 'react'
import type { RefObject } from 'react'
import {
  getPageLayout,
  mmToPx,
  PAPER_SIZES,
  useQuizMeasurement,
} from '@quebon/shared-learning'
import type { QuizScript } from '@quebon/shared-learning'

export interface PaginatedPage<T> {
  pageIndex: number
  items: T[]
  /** 이 페이지 첫 항목의 1-based 문제 번호 */
  startNumber: number
}

export interface PaginateOptions {
  firstPageAvailablePx: number
  contPageAvailablePx: number
  /** 같은 페이지 안에서 문제와 문제 사이에만 적용하는 간격 */
  interItemSpacingPx?: number
  /** 측정 전/실패한 항목에 가정하는 높이 (기본 400) */
  fallbackHeightPx?: number
}

/**
 * 측정된 문제 높이를 기준으로 greedy bin-packing 페이지 분할.
 * - 첫 페이지(헤더 큼)와 연속 페이지(헤더 없음)에 서로 다른 가용높이를 적용한다.
 * - 한 항목이 가용높이를 넘어도 잘라내지 않고 그 항목만 한 페이지에 둔다(페이지당 최소 1).
 *
 * quiz 내용에 의존하지 않으므로(높이만 사용) 제네릭으로 두어 단위 테스트가 쉽다.
 */
export function paginateByHeight<T>(
  items: T[],
  measuredHeights: number[],
  opts: PaginateOptions,
): PaginatedPage<T>[] {
  const fallback = opts.fallbackHeightPx ?? 400
  const interItemSpacing = opts.interItemSpacingPx ?? 0
  const pages: PaginatedPage<T>[] = []
  let pos = 0

  while (pos < items.length) {
    const isFirst = pages.length === 0
    const maxH = isFirst ? opts.firstPageAvailablePx : opts.contPageAvailablePx
    let usedH = 0
    let count = 0

    while (pos + count < items.length) {
      const h = measuredHeights[pos + count] || fallback
      const requiredHeight = h + (count > 0 ? interItemSpacing : 0)
      if (usedH + requiredHeight > maxH && count > 0) break
      usedH += requiredHeight
      count++
    }
    if (count === 0) count = 1 // 페이지당 최소 1문제 보장(overflow는 잘림)

    pages.push({
      pageIndex: pages.length,
      items: items.slice(pos, pos + count),
      startNumber: pos + 1,
    })
    pos += count
  }

  return pages
}

/**
 * Superadmin 교재 레슨 프리뷰와 같은 도련 전 A4 재단 영역을 사용하는 LC 인쇄 규격.
 * LC 인쇄에는 도련을 적용하지 않는다.
 * - 첫 페이지: 헤더 + 본문 상단 여백 이후 y=167부터 문제 시작.
 * - 연속 페이지: 헤더 없음, 상단 여백 80px.
 * - 문제 간격 48px은 문제 사이에만 적용하고 페이지 마지막 문제 뒤에는 적용하지 않는다.
 */
export const LC_PRINT_A4_WIDTH_PX = mmToPx(PAPER_SIZES.A4.width)
export const LC_PRINT_A4_HEIGHT_PX = mmToPx(PAPER_SIZES.A4.height)
export const LC_PRINT_QUIZ_SPACING_PX = 48
export const LC_PRINT_FIRST_PAGE_CONTENT_TOP_PX = 167
export const WRONG_NOTE_CONTINUATION_TOP_PX = 80
const { quizBottom: LC_PRINT_QUIZ_BOTTOM_PX } = getPageLayout(LC_PRINT_A4_HEIGHT_PX)

export const WRONG_NOTE_PAGE_LAYOUT: PaginateOptions = {
  firstPageAvailablePx: LC_PRINT_QUIZ_BOTTOM_PX - LC_PRINT_FIRST_PAGE_CONTENT_TOP_PX,
  contPageAvailablePx: LC_PRINT_QUIZ_BOTTOM_PX - WRONG_NOTE_CONTINUATION_TOP_PX,
  interItemSpacingPx: LC_PRINT_QUIZ_SPACING_PX,
  fallbackHeightPx: 400,
}

/**
 * 오답노트 인쇄 페이지네이션 훅.
 * 화면 밖 measureRef 컨테이너(폭=본문폭)에 각 문제를 간격 없이 `[data-quiz-measure]`로
 * 렌더해 두면 본문 높이를 측정하고, 문제 사이 간격을 더해 A4 페이지 단위로 분할한다.
 *
 * @param deps 재측정 트리거(예: answerKey 토글로 정답란이 생겨 높이가 바뀔 때)
 */
export function useWrongNotePagination(
  measureRef: RefObject<HTMLDivElement | null>,
  quizzes: QuizScript[],
  deps: unknown[] = [],
  firstPageExtraHeightPx = 0,
) {
  const { measuredHeights, isMeasuring } = useQuizMeasurement(measureRef, quizzes, [
    quizzes.length,
    ...deps,
  ])

  const pages = useMemo(
    () => paginateByHeight(quizzes, measuredHeights, {
      ...WRONG_NOTE_PAGE_LAYOUT,
      firstPageAvailablePx: WRONG_NOTE_PAGE_LAYOUT.firstPageAvailablePx - firstPageExtraHeightPx,
    }),
    [quizzes, measuredHeights, firstPageExtraHeightPx],
  )

  return { pages, isMeasuring }
}
